/**
 * Viewport-triggered reveal animation system
 * Kettmeir-inspired curtain wipe with staggered timing
 * - Curtain slides up (faster, power2.out @ ~1s)
 * - Image moves up slightly slower (power4.out @ ~1.2s)
 * - Image scaled up slightly and cropped to prevent gaps
 */

import { gsap } from 'gsap';
import { runWhenPageReady } from '../utils/PageLifecycle';

class ScrollRevealParallax {
  constructor() {
    this.elements = [];
    this.observer = null;
    
    this.init();
  }
  
  init() {
    // Find all parallax wrapper elements
    const wrappers = document.querySelectorAll('[data-reveal-parallax-wrap]');
    
    wrappers.forEach((wrapper, index) => {
      const cover = wrapper.querySelector('[data-reveal-parallax-cover]');
      const image = wrapper.querySelector('[data-reveal-parallax]');
      
      if (!image) return;
      
      this.elements.push({
        wrapper,
        cover,
        image,
        index,
        isRevealed: false
      });
      
      // Set initial states
      this.setInitialState(this.elements[this.elements.length - 1]);
    });
    
    // Set up Intersection Observer
    this.setupObserver();
  }
  
  setInitialState(element) {
    const { cover, image } = element;
    
    // Cover starts fully visible (covering the image)
    if (cover) {
      gsap.set(cover, {
        scaleY: 1,
        transformOrigin: 'top',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 2
      });
    }
    
    // Image starts transformed down and scaled up slightly (for edge cropping)
    gsap.set(image, {
      y: 40, // Start ~40px down (reduced for smoother motion)
      scale: 1.05, // Slight scale to prevent gaps at edges
      opacity: 0
    });
  }
  
  setupObserver() {
    // Observer options - trigger when 20% of element is visible
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.2
    };
    
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Find the element data
          const elementData = this.elements.find(el => el.wrapper === entry.target);
          
          if (elementData && !elementData.isRevealed) {
            this.revealElement(elementData);
          }
        }
      });
    }, options);
    
    // Observe all wrappers
    this.elements.forEach(element => {
      this.observer.observe(element.wrapper);
    });
  }
  
  revealElement(element) {
    const { cover, image, wrapper } = element;
    
    // Mark as revealed so it only happens once
    element.isRevealed = true;
    
    // Unobserve this element (we're done with it)
    this.observer.unobserve(wrapper);
    
    // Staggered animation with elegant timing
    const timeline = gsap.timeline();
    
    // Curtain slides up with smooth easing
    if (cover) {
      timeline.to(cover, {
        scaleY: 0,
        duration: 1.2,
        ease: 'power3.inOut' // Smoother throughout
      }, 0);
    }
    
    // Image moves up with same duration for synchronized finish
    timeline.to(image, {
      y: 0,
      opacity: 1,
      duration: 1.2,
      ease: 'power3.inOut' // Match curtain easing
    }, 0);
    
    // Note: Image stays at scale: 1.05 throughout (set in initial state)
    // This prevents gaps between curtain and image at the top
  }
  
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

runWhenPageReady(() => new ScrollRevealParallax());

export default ScrollRevealParallax;
